import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  LayoutDashboard, Settings, Menu as MenuIcon, Palette, QrCode,
  BarChart3, ShieldCheck, LogOut, Store, Bell, LayoutGrid, Send, Sparkles, ShoppingBag
} from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function DashboardLayout({ children, restaurantId }) {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const params = useParams();
  const rid = restaurantId || params.id;
  const [openReqCount, setOpenReqCount] = useState(0);

  const isPro = user?.role === "admin" || ["pro", "premium", "agency"].includes(user?.plan);
  const hasOrdering = user?.role === "admin" || ["premium", "agency"].includes(user?.plan);

  useEffect(() => {
    if (!rid || !isPro) return;
    let cancelled = false;
    const fetchCount = () => {
      api.get(`/restaurants/${rid}/service-requests`, { params: { status: "open" } })
        .then((r) => { if (!cancelled) setOpenReqCount(r.data.length); })
        .catch(() => {});
    };
    fetchCount();
    const t = setInterval(fetchCount, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [rid, isPro]);

  const baseLinks = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("common.dashboard") },
  ];
  const restaurantLinks = rid ? [
    { to: `/dashboard/restaurants/${rid}`, icon: Settings, label: t("common.settings") },
    { to: `/dashboard/restaurants/${rid}/menu`, icon: MenuIcon, label: t("dashboard.menuBuilder") },
    { to: `/dashboard/restaurants/${rid}/themes`, icon: Sparkles, label: lang === "de" ? "Themes (Beta)" : lang === "tr" ? "Temalar (Beta)" : "Themes (Beta)" },
    { to: `/dashboard/restaurants/${rid}/qr`, icon: QrCode, label: t("dashboard.qrCode") },
    ...(isPro ? [{
      to: `/dashboard/restaurants/${rid}/requests`,
      icon: Bell,
      label: lang === "de" ? "Tisch-Anfragen" : lang === "tr" ? "Masa Çağrıları" : "Table requests",
      badge: openReqCount,
    }, {
      to: `/dashboard/restaurants/${rid}/telegram`,
      icon: Send,
      label: "Telegram",
    }] : []),
    ...(hasOrdering ? [{
      to: `/dashboard/restaurants/${rid}/orders`,
      icon: ShoppingBag,
      label: t("orders.title"),
    }] : []),
    { to: `/dashboard/restaurants/${rid}/stats`, icon: BarChart3, label: t("dashboard.statistics") },
  ] : [];
  const adminLinks = user?.role === "admin"
    ? [
        { to: "/dashboard/admin", icon: ShieldCheck, label: t("dashboard.adminPanel") },
        { to: "/dashboard/admin/subscriptions", icon: ShieldCheck, label: "Subscriptions" },
      ]
    : [];
  const links = [...baseLinks, ...restaurantLinks, ...adminLinks];

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 lg:px-8 py-6 pb-32 lg:pb-12">
        <div className="grid lg:grid-cols-[240px_1fr] gap-6">
          <aside className="lg:sticky lg:top-20 lg:self-start" data-testid="dashboard-sidebar">
            <div className="rounded-2xl border bg-card p-2 lg:p-3">
              <div className="hidden lg:block px-3 py-3 border-b mb-2">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                  {user?.name}
                </div>
                <div className="text-sm font-medium truncate">{user?.email}</div>
                <div className="mt-1 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-600 dark:text-purple-400 font-semibold">
                  <Store className="h-3 w-3" />
                  {user?.plan?.toUpperCase()}
                </div>
              </div>
              <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible scrollbar-hide">
                {links.map((l) => {
                  const active = loc.pathname === l.to;
                  const Icon = l.icon;
                  return (
                    <Link key={l.to} to={l.to} data-testid={`sidebar-${l.to.split("/").pop()}`} className="shrink-0">
                      <Button
                        variant={active ? "secondary" : "ghost"}
                        size="sm"
                        className={`w-full justify-center lg:justify-start gap-2 rounded-xl px-3 lg:px-3 ${active ? "bg-purple-500/10 text-purple-600 dark:text-purple-400 hover:bg-purple-500/15" : ""}`}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="text-sm whitespace-nowrap">{l.label}</span>
                        {l.badge > 0 && (
                          <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-500 text-white animate-pulse" data-testid={`badge-${l.to.split("/").pop()}`}>
                            {l.badge}
                          </span>
                        )}
                      </Button>
                    </Link>
                  );
                })}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="w-full justify-center lg:justify-start gap-2 rounded-xl text-destructive hover:text-destructive lg:mt-2 shrink-0 px-3"
                  data-testid="sidebar-logout"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span className="text-sm whitespace-nowrap">{t("common.logout")}</span>
                </Button>
              </nav>
            </div>
          </aside>
          <main data-testid="dashboard-main">{children}</main>
        </div>
      </div>
    </div>
  );
}
